import React from 'react';
import {
    Card,
    MultiSelectSearch,
    DatePeriodSearch,
    StatusSearch,
    DefaultMultiSelectSearch,
    SimpleTable,
    DateTimeColumn,
    StatusColumn, WarningModal,
} from 'components';
import { connect } from 'react-redux';
import { IRootState, Dispatch } from '../../redux/store/configureStore';
import { useEffectProcessing, useLocalErrorHandler, useMultiSearch } from 'hooks';
import { IExamsFilters, IFilterPart, IFromToDatePart } from 'components/models';
import { FilteredExamStatus, MappedFilteredExamStatus, getExamStatusMapping } from 'components/enums';
import { PaginationImpl } from 'components/defaultValues';
import newsIcon from 'components/assets/svg/news_icon.svg';
import { ExamDetailsContainer } from '../ExamDetailsContainer';

const ExamListContainer = ({
                               getExams, setErrorHandler, examList, apiError, ...props
                           }: Props) => {
    useLocalErrorHandler(setErrorHandler);
    const [selectedId, selectId] = React.useState<string | null>(null);
    const {
        filters: examsFilters, filterSetter, changeFilter, changeFilterField,
    } = useMultiSearch<IExamsFilters & IFromToDatePart & IFilterPart>(getExams, {});

    const processing = useEffectProcessing(async () => {
        await getExams({});
        await props.getQualificationsAsync({});
        await props.getAccreditationCentersAsync({});
    }, []);

    const HEADER =  props.ascContext ? [
        { title: 'Период проведения' },
        { title: 'Статус' },
    ]: [
        { title: 'Период проведения' },
        { title: 'Специальность' },
        { title: 'Статус' },
    ];

    const rows = examList ? examList.items.map(exam => ({
        id: exam.id,
        columns: props.ascContext ?  [
            <DateTimeColumn date={exam.date} />,
            <StatusColumn status={getExamStatusMapping(exam.status, exam.currentStageNumber)} />,
        ] : [
            <DateTimeColumn date={exam.date} />,
            exam.qualificationTitle,
            <StatusColumn status={getExamStatusMapping(exam.status, exam.currentStageNumber)} />,
        ], 
    })) : [];

    const examStatusesOptions = Object.keys(MappedFilteredExamStatus).map(k => ({
        value: k,
        label: MappedFilteredExamStatus[k].text,
    }));

    examStatusesOptions.unshift({value: '', label: 'Все'});

    return (
        <Card
            withoutPadding
            title="Список аккредитаций"
            category="Поиск и просмотр"
            apiError={apiError}
            showLoader={processing}
        >
            <MultiSelectSearch>
                <StatusSearch<FilteredExamStatus>
                    value={examsFilters.filteredExamStatus}
                    options={examStatusesOptions}
                    onChange={changeFilterField('filteredExamStatus')}
                />
                <DatePeriodSearch
                    onChange={changeFilter}
                    from={examsFilters.from}
                    to={examsFilters.to}
                    setFrom={filterSetter('from')}
                    setTo={filterSetter('to')}
                />
                {!props.ascContext && (<DefaultMultiSelectSearch
                    label="СПЕЦИАЛЬНОСТЬ"
                    placeholder="Выберите одну или несколько специальностей"
                    onChange={changeFilterField('qualificationIds')}
                    value={examsFilters.qualificationIds || undefined}
                    options={props.qualifications ? props.qualifications.items.map(i => ({ value: i.id, label: `${i.regNumber} - ${i.title}` })) : []}
                />)}
                <DefaultMultiSelectSearch
                    label="АЦ"
                    placeholder="Выберите один или несколько аккредитационных центров"
                    onChange={changeFilterField('accreditationCenterIds')}
                    value={examsFilters.accreditationCenterIds || undefined}
                    options={props.accreditationCenters ? props.accreditationCenters.items.map(i => ({ value: i.id, label: i.fullName })) : []}
                />
            </MultiSelectSearch>

            <SimpleTable
                tableRows={rows}
                tableHeaders={HEADER}
                handleRowClick={id => selectId(String(id))}
                placeholderTitle="Нет ничего в списке"
                placeholderText={`Здесь будут отображаться все аккредитации, связанные с ${props.ascContext ? 'АПК': 'АК'}`}
                placeholderIcon={newsIcon}
                pagination={new PaginationImpl(examList).getPaginationData()}
                onPagination={p => changeFilter({ page: p.page, pageSize: p.perPage })}
            />

            <WarningModal
                large
                card
                withoutCancelButton
                title={props.examDetails ? props.examDetails.attestationSubCommissionName : ''}
                show={!!selectedId}
                onHide={() => selectId(null)}
            >
                {selectedId && (
                    <ExamDetailsContainer
                        examId={selectedId}
                    />
                )}
            </WarningModal>
        </Card>
    );
};

const mapState = (state: IRootState) => ({
    ascContext: state.auth.auth && !!state.auth.auth.profile.attestationSubCommissionId,
    examDetails: state.exams.examDetails,
    examList: state.exams.exams,
    apiError: state.errors.error,
    qualifications: state.qualifications.qualifications,
    accreditationCenters: state.accreditationCenters.accreditationCenters,
});

const mapDispatch = (dispatch: Dispatch) => ({
    getExams: dispatch.exams.getExamFilteredList,
    setErrorHandler: dispatch.errors.setErrorHandler,
    getQualificationsAsync: dispatch.qualifications.getQualifications,
    getAccreditationCentersAsync: dispatch.accreditationCenters.getAccreditationCenters,
});

export default connect(mapState, mapDispatch)(ExamListContainer);

type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
